import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star rating component
const StarRating = ({ rating }) => {
  return (
    <div className="flex gap-0.5" aria-label={`Note: ${rating} sur 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={14}
          className={star <= rating ? 'fill-[var(--gold)] text-[var(--gold)]' : 'text-[var(--champagne)]'}
        />
      ))}
    </div>
  );
};

// Single review card
const ReviewCard = ({ review, isVisible }) => {
  // Generate initials from name
  const initials = review.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isVisible ? 1 : 0.4, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="bg-white/80 backdrop-blur-sm border border-[var(--champagne)] p-6 h-full flex flex-col shadow-sm hover:shadow-md transition-shadow"
      data-testid={`review-card-${review.id}`}
    >
      {/* Quote icon */}
      <Quote 
        size={24} 
        className="text-[var(--gold)]/30 mb-3 transform rotate-180" 
      />
      
      {/* Comment */}
      <p className="text-[var(--charcoal)]/80 text-sm leading-relaxed flex-1 mb-4 italic">
        &ldquo;{review.comment}&rdquo;
      </p>
      
      {/* Footer: Avatar, Name, Rating, Source */}
      <div className="flex items-center gap-3 pt-4 border-t border-[var(--champagne)]/50">
        {/* Avatar or Initials */}
        {review.avatar_url ? (
          <img 
            src={review.avatar_url} 
            alt={review.name}
            className="w-10 h-10 rounded-full object-cover border border-[var(--champagne)]"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--gold)]/20 to-[var(--dark-rosegold)]/20 flex items-center justify-center border border-[var(--champagne)]">
            <span className="text-xs font-medium text-[var(--dark-rosegold)]">
              {initials}
            </span>
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <p className="font-medium text-[var(--charcoal)] text-sm truncate">
            {review.name}
          </p>
          <div className="flex items-center gap-2">
            <StarRating rating={review.rating} />
            {review.source && (
              <span className="text-[10px] text-[var(--charcoal)]/50 uppercase tracking-wider">
                {review.source}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function ReviewsCarousel() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();

  // Responsive: number of visible cards
  const [visibleCount, setVisibleCount] = useState(3);

  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/reviews?limit=20`);
        setReviews(response.data || []);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  // Handle responsive visible count
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setVisibleCount(1);
      } else if (width < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigation functions
  const maxIndex = Math.max(0, reviews.length - visibleCount);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  // Autoplay
  useEffect(() => {
    if (shouldReduceMotion || isPaused || reviews.length <= visibleCount) return;

    const interval = setInterval(() => {
      goToNext();
    }, 4000); // 4 seconds per slide

    return () => clearInterval(interval);
  }, [goToNext, isPaused, reviews.length, visibleCount, shouldReduceMotion]);

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      goToPrev();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    }
  };

  // Don't render if no reviews
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (reviews.length === 0) {
    return null; // Don't render the section if no reviews
  }

  // Get visible reviews
  const visibleReviews = reviews.slice(currentIndex, currentIndex + visibleCount);
  
  // If we're near the end, wrap around
  if (visibleReviews.length < visibleCount && reviews.length > visibleCount) {
    const remaining = visibleCount - visibleReviews.length;
    visibleReviews.push(...reviews.slice(0, remaining));
  }

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label="Carrousel des avis clients"
      ref={containerRef}
      data-testid="reviews-carousel"
    >
      {/* Cards container */}
      <div className="overflow-hidden">
        <motion.div 
          className="flex gap-4 md:gap-6"
          animate={{ 
            x: shouldReduceMotion ? 0 : undefined 
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <AnimatePresence mode="popLayout">
            {visibleReviews.map((review, index) => (
              <div
                key={`${review.id}-${index}`}
                className="flex-shrink-0"
                style={{ 
                  width: `calc((100% - ${(visibleCount - 1) * (visibleCount > 1 ? 1.5 : 0)}rem) / ${visibleCount})`
                }}
              >
                <ReviewCard review={review} isVisible={true} />
              </div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Navigation arrows - Only show if more reviews than visible */}
      {reviews.length > visibleCount && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 w-10 h-10 bg-white/90 backdrop-blur-sm border border-[var(--champagne)] rounded-full flex items-center justify-center text-[var(--charcoal)] hover:bg-[var(--gold)] hover:text-white hover:border-[var(--gold)] transition-all shadow-lg z-10"
            aria-label="Avis précédent"
            data-testid="carousel-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 w-10 h-10 bg-white/90 backdrop-blur-sm border border-[var(--champagne)] rounded-full flex items-center justify-center text-[var(--charcoal)] hover:bg-[var(--gold)] hover:text-white hover:border-[var(--gold)] transition-all shadow-lg z-10"
            aria-label="Avis suivant"
            data-testid="carousel-next"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Pagination dots */}
      {reviews.length > visibleCount && (
        <div className="flex justify-center gap-2 mt-6" role="tablist" aria-label="Navigation des avis">
          {Array.from({ length: Math.ceil(reviews.length / visibleCount) }).map((_, idx) => {
            const isActive = Math.floor(currentIndex / visibleCount) === idx;
            return (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx * visibleCount)}
                className={`w-2 h-2 rounded-full transition-all ${
                  isActive 
                    ? 'bg-[var(--gold)] w-6' 
                    : 'bg-[var(--champagne)] hover:bg-[var(--dark-rosegold)]'
                }`}
                role="tab"
                aria-selected={isActive}
                aria-label={`Page ${idx + 1}`}
                data-testid={`carousel-dot-${idx}`}
              />
            );
          })}
        </div>
      )}

      {/* Reduced motion notice (visually hidden) */}
      {shouldReduceMotion && (
        <span className="sr-only">
          Animation réduite activée. Utilisez les flèches pour naviguer.
        </span>
      )}
    </div>
  );
}
